// Bảng giá (GĐ5, §5.2–5.4) — mọi đơn vị:
//  · "Giá nhập của tôi" (chỉ đọc) + các lần đổi giá đã hẹn.
//  · Đại lý / CTV: thư viện bảng giá cho cấp dưới (Đại lý → CTV & Shop; CTV → Shop), gán cho
//    cấp dưới trực tiếp, chuyển hàng loạt, lưu trữ.
//  · Đại lý: xem (chỉ đọc) bảng các CTV của mình giao cho shop.
// Không bao giờ thấy giá nhập của cấp trên hay giá gốc BEST.

import { useMemo, useState } from 'react'
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { AlertTriangle, ChevronDown, Eye, Network, Store, Users, Wallet } from 'lucide-react'
import { toast } from 'sonner'
import { get } from '@/lib/api'
import { getAuthState } from '@/lib/auth'
import { onlyWorkMode, useWorkMode } from '@/lib/workMode'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { IndexBadge } from '@/components/ui/data-table'
import { PageHeader } from '@/components/ui/page-header'
import { LoadError } from '@/components/ui/data-notice'
import { EmptyState } from '@/components/ui/empty-state'
import { ViewSwitcher, type ViewOption } from '@/components/ui/view-switcher'
import { PriceListEditor, cellsBelow, normalizePriceListData, type PriceGrid, type PriceListData } from '@/components/PriceListEditor'
import { PriceListFormDialog, type CopySource } from '@/components/price-lists/PriceListFormDialog'
import { PriceListDetailDialog } from '@/components/price-lists/PriceListDetailDialog'
import { useUnitCan } from '@/lib/unitAccess'
import {
  KIND_LABEL,
  fmtDateTime,
  type LibraryKind,
  type LibraryRow,
  type MineResponse,
  type NetworkUnit,
  type SaveResponse,
  type UnitType,
} from '@/components/price-lists/types'

type View = 'mine' | LibraryKind | 'network'

interface NetworkList {
  id: string
  code: string
  name: string
  kind: string
  data: PriceListData
  grid?: PriceGrid
  gridVersion: number
  version: number
  ownerShopId: string | null
  ownerName: string | null
  unitCount: number
}

export default function PriceListsPage() {
  const auth = getAuthState()
  const me = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const myType = (me?.tenantType ?? 'shop') as UnitType
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): thư viện bảng giá cấp dưới cần quyền lập bảng giá cấp dưới;
  // không có thì chỉ xem "Giá nhập của tôi" như Shop
  const { can } = useUnitCan()
  const libraryKinds: LibraryKind[] = !can('network.pricing') ? [] : myType === 'agency' ? ['ctv', 'shop'] : myType === 'ctv' ? ['shop'] : []
  const hasLibrary = libraryKinds.length > 0
  // Chế độ Bán hàng: chỉ "Giá nhập của tôi" (như Shop). Quản lý: mở sẵn thư viện cho cấp dưới.
  const storedMode = useWorkMode()
  const workMode = onlyWorkMode(me?.workModes) ?? storedMode
  const sellOnly = hasLibrary && workMode === 'sell'
  const [pickedView, setView] = useState<View>(() => (hasLibrary && workMode === 'manage' ? libraryKinds[0]! : 'mine'))
  const view: View = sellOnly ? 'mine' : pickedView

  const mineQ = useQuery({ queryKey: ['price-lists', 'mine'], queryFn: () => get<MineResponse>('/price-lists/mine') })
  const gridQ = useQuery({ queryKey: ['price-lists', 'grid'], queryFn: () => get<{ grid: PriceGrid | null }>('/price-lists/grid'), enabled: hasLibrary })
  const libQ = useQuery({
    queryKey: ['price-lists', 'library'],
    queryFn: () => get<{ priceLists: LibraryRow[] }>('/price-lists/library'),
    enabled: hasLibrary,
  })
  const unitsQ = useQuery({ queryKey: ['network-units'], queryFn: () => get<{ units: NetworkUnit[] }>('/network/units'), enabled: hasLibrary })

  const mine = mineQ.data?.mine ?? null
  const library = libQ.data?.priceLists ?? []

  const options: ViewOption[] = [
    { value: 'mine', label: 'Giá nhập của tôi', icon: Wallet, tone: 'orange' },
    ...libraryKinds.map<ViewOption>((k) => ({
      value: k,
      label: `Bảng giá cho ${KIND_LABEL[k]}`,
      icon: k === 'ctv' ? Users : Store,
      tone: 'violet',
      count: library.filter((l) => l.kind === k).length,
    })),
    ...(myType === 'agency'
      ? [{ value: 'network', label: 'Bảng CTV giao cho shop', icon: Network, tone: 'blue' } as ViewOption]
      : []),
  ]

  return (
    <div className="space-y-6">
      <PageHeader title="Bảng giá" />

      {hasLibrary && !sellOnly && <ViewSwitcher value={view} onChange={(v) => setView(v as View)} options={options} />}

      {view === 'mine' && <MyBuyPrice q={mineQ} />}
      {hasLibrary && (view === 'ctv' || view === 'shop') && (
        <LibrarySection
          key={view}
          kind={view}
          grid={gridQ.data?.grid ?? null}
          gridLoading={gridQ.isLoading}
          libQ={libQ}
          mine={mineQ.isSuccess ? mine : undefined}
          units={unitsQ.data?.units ?? []}
        />
      )}
      {view === 'network' && myType === 'agency' && <NetworkLists grid={gridQ.data?.grid ?? null} />}
    </div>
  )
}

// ─── Giá nhập của tôi ────────────────────────────────────────────

function MyBuyPrice({ q }: { q: UseQueryResult<MineResponse> }) {
  const [preview, setPreview] = useState<number | null>(null)
  if (q.isError) return <LoadError onRetry={() => q.refetch()}>Không tải được bảng giá nhập.</LoadError>
  if (q.isLoading) return <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
  const mine = q.data?.mine
  if (!mine) return <EmptyState title="Bạn chưa được giao bảng giá." className="rounded-xl bg-raise shadow-card" />
  const cur = mine.current
  const pendingShown = mine.pending.find((p) => p.version === preview)

  return (
    <section className="space-y-4 rounded-xl bg-raise p-4 shadow-card sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-medium text-ink">{mine.priceList.name}</h2>
          <p className="text-xs text-inkSoft">
            <span className="font-mono">{mine.priceList.code}</span>
            {cur ? ` · phiên bản v${cur.version}, hiệu lực từ ${fmtDateTime(cur.effectiveFrom)}` : ''}
            {cur?.changeNote ? ` · ${cur.changeNote}` : ''}
          </p>
        </div>
        <span className="rounded-full bg-paperSoft px-2.5 py-1 text-xs text-inkSoft">Chỉ xem</span>
      </div>

      {mine.pending.map((p) => {
        const diff = cur ? countDiff(cur.grid, cur.data, p.grid ?? cur.grid, p.data) : null
        return (
          <div key={p.version} className="space-y-2 rounded-lg bg-warn/10 p-3 text-sm text-ink">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warn" />
                <span>
                  Từ <strong>{fmtDateTime(p.effectiveFrom)}</strong> giá nhập đổi
                  {diff ? ` (tăng ${diff.up} ô, giảm ${diff.down} ô)` : ''}
                  {p.changeNote ? ` — ${p.changeNote}` : ''}.
                </span>
              </p>
              <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setPreview(preview === p.version ? null : p.version)}>
                <Eye className="size-4" />
                {preview === p.version ? 'Ẩn bản xem trước' : 'Xem trước'}
              </Button>
            </div>
            {pendingShown?.version === p.version && cur && <PriceListEditor grid={p.grid ?? cur.grid} value={p.data} readOnly />}
          </div>
        )
      })}

      {cur ? (
        <PriceListEditor grid={cur.grid} value={cur.data} readOnly />
      ) : (
        <p className="text-sm text-muted">Bảng này chưa có phiên bản đang hiệu lực.</p>
      )}
    </section>
  )
}

function countDiff(fromGrid: PriceGrid, from: PriceListData, toGrid: PriceGrid, to: PriceListData) {
  return { up: cellsBelow(fromGrid, from, to, toGrid).length, down: cellsBelow(toGrid, to, from, fromGrid).length }
}

// ─── Thư viện của tôi ────────────────────────────────────────────

function LibrarySection({
  kind,
  grid,
  gridLoading,
  libQ,
  mine,
  units,
}: {
  kind: LibraryKind
  grid: PriceGrid | null
  gridLoading: boolean
  libQ: UseQueryResult<{ priceLists: LibraryRow[] }>
  /** undefined = đang tải */
  mine: MineResponse['mine'] | undefined
  units: NetworkUnit[]
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState<{ list: LibraryRow | null } | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const library = libQ.data?.priceLists ?? []
  const rows = library.filter((l) => l.kind === kind)

  // Giá nhập của tôi — mỗi bảng có hàng cân riêng, so khác hàng được (máy chủ vẫn kiểm chính xác).
  const cur = mine?.current ?? null
  const buyGrid = cur?.grid ?? null
  const buyData = cur && buyGrid ? normalizePriceListData(buyGrid, cur.data) : null
  const pendingBuy = cur && buyGrid ? (mine?.pending ?? []).map((p) => ({ ...p, grid: p.grid ?? buyGrid, data: normalizePriceListData(p.grid ?? buyGrid, p.data) })) : []
  const rowsOf = (l: { grid?: PriceGrid }) => l.grid ?? grid ?? buyGrid

  const copySources: CopySource[] = useMemo(
    () => [
      ...(buyData && buyGrid ? [{ id: '__buy__', label: 'Giá nhập của tôi', data: buyData, grid: buyGrid }] : []),
      ...library.flatMap((l) => {
        const g = rowsOf(l)
        return g ? [{ id: l.id, label: `${l.name} (${l.code})`, data: l.data, grid: g }] : []
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buyData, buyGrid, library, grid],
  )

  function onSaved(res: SaveResponse) {
    toast.success(
      res.scheduled ? `Đã lưu ${res.priceList.code} — giá mới áp dụng từ ${fmtDateTime(res.scheduled)}` : form?.list ? `Đã lưu ${res.priceList.code}` : `Đã tạo bảng ${res.priceList.code}`,
    )
    setForm(null)
    qc.invalidateQueries({ queryKey: ['price-lists'] })
  }

  const canCreate = !!buyData

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button onClick={() => setForm({ list: null })} disabled={!canCreate}>
          Tạo bảng cho {KIND_LABEL[kind]}
        </Button>
      </div>

      {mine === null && <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">Bạn chưa được giao bảng giá nhập.</p>}
      {mine && !buyData && !gridLoading && (
        <p className="rounded-lg bg-warn/10 px-3 py-2 text-sm text-ink">Giá nhập của bạn chưa có phiên bản đang hiệu lực.</p>
      )}

      {libQ.isError ? (
        <LoadError onRetry={() => libQ.refetch()}>Không tải được thư viện bảng giá.</LoadError>
      ) : libQ.isLoading ? (
        <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          className="rounded-xl bg-raise shadow-card"
          title={`Chưa có bảng giá cho ${KIND_LABEL[kind]}`}
        />
      ) : (
        <ul className="divide-y divide-rule overflow-hidden rounded-xl bg-raise shadow-card">
          {rows.map((l, i) => {
            const lg = rowsOf(l)
            const belowNow = lg && buyData ? cellsBelow(lg, l.data, buyData, buyGrid).length : 0
            // Lần đổi giá nhập đã hẹn đầu tiên làm bảng này thành dưới giá nhập (P3/P4)
            const nextHit = lg ? pendingBuy.map((p) => ({ at: p.effectiveFrom, n: cellsBelow(lg, l.data, p.data, p.grid).length })).find((x) => x.n > 0) : undefined
            return (
              <li key={l.id}>
                <button
                  type="button"
                  onClick={() => setDetailId(l.id)}
                  className="motion-state flex w-full flex-wrap items-center gap-x-4 gap-y-1 p-4 text-left hover:bg-paperSoft"
                >
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="flex items-center gap-2 font-medium text-ink">
                      <IndexBadge n={i + 1} />
                      <span className="min-w-0 flex-1 truncate">{l.name}</span>
                    </p>
                    <p className="text-xs text-inkSoft">
                      <span className="font-mono">{l.code}</span> · cập nhật {fmtDateTime(l.updatedAt)} · v{l.version}
                    </p>
                    {belowNow > 0 && <p className="text-xs font-medium text-danger">Đang thấp hơn giá nhập của bạn ở {belowNow} ô</p>}
                    {belowNow === 0 && nextHit && (
                      <p className="text-xs font-medium text-warn">
                        Từ {fmtDateTime(nextHit.at)} sẽ thấp hơn giá nhập mới ở {nextHit.n} ô
                      </p>
                    )}
                  </div>
                  <span className={cn('text-sm tabular-nums', l.unitCount > 0 ? 'text-ink' : 'text-muted')}>
                    {l.unitCount} đơn vị
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {form && (form.list ? rowsOf(form.list) : buyGrid ?? grid) && (
        <PriceListFormDialog
          grid={(form.list ? rowsOf(form.list) : buyGrid ?? grid)!}
          kind={kind}
          list={form.list}
          buyData={buyData}
          buyGrid={buyGrid}
          copySources={copySources}
          onClose={() => setForm(null)}
          onSaved={onSaved}
        />
      )}
      {detailId && (
        <PriceListDetailDialog
          id={detailId}
          grid={grid}
          library={library}
          units={units}
          onClose={() => setDetailId(null)}
          onEdit={(row) => {
            setDetailId(null)
            setForm({ list: row })
          }}
        />
      )}
    </section>
  )
}

// ─── Bảng CTV giao cho shop (Đại lý, chỉ đọc) ─────────────────────

function NetworkLists({ grid }: { grid: PriceGrid | null }) {
  const q = useQuery({ queryKey: ['price-lists', 'network'], queryFn: () => get<{ priceLists: NetworkList[] }>('/price-lists/network') })
  const [open, setOpen] = useState<string | null>(null)
  if (q.isError) return <LoadError onRetry={() => q.refetch()}>Không tải được bảng của CTV.</LoadError>
  if (q.isLoading) return <p className="p-8 text-center text-sm text-muted">Đang tải…</p>
  const lists = q.data?.priceLists ?? []
  if (lists.length === 0) return <EmptyState className="rounded-xl bg-raise shadow-card" title="Chưa có bảng giá" />

  return (
    <section className="space-y-3">
      <ul className="divide-y divide-rule overflow-hidden rounded-xl bg-raise shadow-card">
        {lists.map((l, i) => (
          <li key={l.id}>
            <button
              type="button"
              onClick={() => setOpen(open === l.id ? null : l.id)}
              aria-expanded={open === l.id}
              className="motion-state flex w-full flex-wrap items-center gap-x-4 gap-y-1 p-4 text-left hover:bg-paperSoft"
            >
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex items-center gap-2 font-medium text-ink">
                  <IndexBadge n={i + 1} />
                  <span className="min-w-0 flex-1 truncate">{l.name}</span>
                </p>
                <p className="text-xs text-inkSoft">
                  <span className="font-mono">{l.code}</span> · CTV {l.ownerName ?? '—'} · v{l.version}
                </p>
              </div>
              <span className="text-sm tabular-nums text-inkSoft">{l.unitCount} shop</span>
              <ChevronDown className={cn('size-4 text-muted transition-transform', open === l.id && 'rotate-180')} />
            </button>
            {open === l.id && (
              <div className="px-4 pb-4">
                {l.grid ?? grid ? (
                  <PriceListEditor grid={(l.grid ?? grid)!} value={l.data} readOnly />
                ) : (
                  <p className="text-sm text-muted">Chưa hiển thị được bảng này.</p>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
